import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import type { AppActionConfig, Message, MessageButtonSpec, SourceRef } from '@captivateiq/assistant/types';
import {
  FEATURE_FLAG_AGENT_CHARTING,
  FEATURE_FLAG_AI_COMP_ADMIN_AGENT,
  isFeatureEnabled,
} from '@captivateiq/assistant/utils/featureFlags';
import { QBox, QStack } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import { FeedbackButtons } from '../../../shared/FeedbackButtons';
import { AppActionLabel } from './AppActionLabel';
import { ContentBlockRenderer } from './ContentBlocks/ContentBlockRenderer';
import { MessageButtonBar } from './MessageButtonBar';
import { SourceCitationChip } from './SourceCitationChip';
import { SourcesFooter } from './SourcesFooter';
import { expandedTextColumnSx } from '../../shared/expandedTextColumnSx';
import { injectCitationMarkers } from '../../../../utils/citations';

type AgentMessageProps = {
  message: Message;
  buttons?: MessageButtonSpec[];
  appActions?: AppActionConfig[];
  feedback?: number | null;
  feedbackResponse?: string | null;
  onFeedback?: (messageId: string, feedback: number) => void;
  onFeedbackResponse?: (messageId: string, text: string) => Promise<void>;
  /** True only for the response that just finished generating — types itself out word by word. */
  animateReveal?: boolean;
};

// Matches each word plus any trailing whitespace (including newlines), so re-joining a prefix of
// tokens reproduces the original markdown's exact spacing — never split mid-word. Revealing by
// word (not character) keeps the re-render/re-parse count reasonable for long responses.
const REVEAL_TOKEN_PATTERN = /\S+\s*/g;
// Proportional "catch-up" reveal: each frame shows 1/N of whatever's still unrevealed. Large
// remaining amounts move in big jumps (a long response doesn't drag), and as the remainder
// shrinks the steps shrink with it (floor of 1 word/frame guarantees it still finishes).
const REVEAL_CATCHUP_DIVISOR = 20;
// Caps how often the reveal commits to React state (and re-renders/re-parses the markdown tree)
// while still advancing every animation frame internally — ~20fps reads as smooth for text but
// cuts full markdown re-parses roughly 3-4x versus committing on every single frame.
const REVEAL_RENDER_THROTTLE_MS = 50;

const sanitizationSchema = {
  ...defaultSchema,
  tagNames: [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    'del',
    's',
    'mark',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'a',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
    'blockquote',
    'hr',
    'code',
    'pre',
    'span',
    'sup',
  ],
  attributes: {
    // Inherits from hast-util-sanitize's defaultSchema — verify its changelog when upgrading rehype-sanitize
    ...defaultSchema.attributes,
    span: ['className'],
    sup: ['className', 'dataSourceIndex'],
  },
};

const defaultMarkdownComponents = {
  table: ({ children }: React.TableHTMLAttributes<HTMLTableElement>) => (
    <QBox
      sx={{
        width: '100%',
        overflowX: 'auto',
        marginBottom: '0.5rem',
        scrollbarWidth: 'thin',
        // Horizontal scroll shadows with theme color
        backgroundImage: `
          linear-gradient(90deg, rgba(251,251,251,1) 30%, rgba(251,251,251,0)),
          linear-gradient(90deg, rgba(251,251,251,0), rgba(251,251,251,1) 70%),
          radial-gradient(farthest-side at 0% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0)),
          radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0))
        `,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '30px 100%, 30px 100%, 10px 100%, 10px 100%',
        backgroundPosition: '0 0, 100% 0, 0 0, 100% 0',
        backgroundAttachment: 'local, local, scroll, scroll',
        '.statement-elevated-section &': {
          // on white background
          backgroundImage: `
          linear-gradient(90deg, rgba(255,255,255,1) 30%, rgba(255,255,255,0)),
          linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,1) 70%),
          radial-gradient(farthest-side at 0% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0)),
          radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0))
        `,
        },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
        },
      }}
    >
      <table>{children}</table>
    </QBox>
  ),
};

const SourcesContext = createContext<SourceRef[]>([]);

const SupWithCitation = ({ children, node, ...props }: React.HTMLAttributes<HTMLElement> & { node?: unknown }) => {
  const sources = useContext(SourcesContext);
  const sourceIndex = (props as Record<string, unknown>)['data-source-index'];
  if (sourceIndex !== undefined && sources.length > 0) {
    const idx = Number(sourceIndex);
    const sourceRef = sources[idx - 1];
    if (sourceRef) {
      return (
        <SourceCitationChip
          dataContext={sourceRef.data_context}
          index={idx}
          label={sourceRef.label}
          source={sourceRef.url}
        />
      );
    }
  }
  return <sup {...props}>{children}</sup>;
};

const markdownComponents = {
  ...defaultMarkdownComponents,
  sup: SupWithCitation,
};

export const AgentMessage = ({
  message,
  buttons = [],
  appActions = [],
  feedback,
  feedbackResponse,
  onFeedback,
  onFeedbackResponse,
  animateReveal = false,
}: AgentMessageProps) => {
  const { features, isExpanded } = useAIShellContext();
  const citationChipsEnabled = features.citationChips ?? false;
  const sources = useMemo(
    () => (citationChipsEnabled ? message.cited_sources ?? [] : []),
    [citationChipsEnabled, message.cited_sources],
  );

  const processedContents = useMemo(
    () => injectCitationMarkers(message.contents, sources.length),
    [message.contents, sources.length],
  );

  const [isRevealing, setIsRevealing] = useState(animateReveal);
  const revealTokens = useMemo(
    () => processedContents.match(REVEAL_TOKEN_PATTERN) ?? [processedContents],
    [processedContents],
  );
  const [revealedWordCount, setRevealedWordCount] = useState(animateReveal ? 0 : revealTokens.length);
  const hasStartedRevealRef = useRef(false);

  useEffect(() => {
    if (!animateReveal || hasStartedRevealRef.current) return;
    hasStartedRevealRef.current = true;

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setRevealedWordCount(revealTokens.length);
      setIsRevealing(false);
      return;
    }

    // animateReveal only flips true a render after this message first mounts (the parent learns
    // which message is "new" via its own effect), so the initial useState values above are
    // already stale by the time we get here — set both explicitly rather than trust them.
    setIsRevealing(true);
    setRevealedWordCount(0);

    const totalWords = revealTokens.length;
    let cancelled = false;
    let shownWords = 0;
    let lastRenderTime = 0;
    let frameId: number;

    const tick = (now: number) => {
      if (cancelled) return;
      if (shownWords < totalWords) {
        // Reveal proportionally slower at the start and faster as it goes — a long response
        // doesn't sit forever on its opening words, and it still visibly "types" rather than
        // dumping instantly. Floor of 1 word/frame guarantees forward progress from word one.
        const step = Math.max(1, Math.ceil(shownWords / REVEAL_CATCHUP_DIVISOR));
        shownWords = Math.min(totalWords, shownWords + step);
      }
      const isDone = shownWords >= totalWords;
      // The word-count math above still advances every frame for a smooth catch-up curve, but
      // committing it to React state re-renders the whole markdown tree (rehype/remark included)
      // — capping that to ~20fps keeps the reveal just as visibly smooth while cutting the number
      // of full re-parses roughly 3-4x for longer responses.
      if (isDone || now - lastRenderTime >= REVEAL_RENDER_THROTTLE_MS) {
        lastRenderTime = now;
        setRevealedWordCount(shownWords);
      }
      if (isDone) {
        setIsRevealing(false);
        return;
      }
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
    // revealTokens intentionally excluded: this message's text never changes after it
    // arrives, and re-running on every identity change would restart the whole animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animateReveal]);

  const displayedContents = isRevealing ? revealTokens.slice(0, revealedWordCount).join('') : processedContents;

  return (
    <QStack direction="column" spacing="0px">
      <AppActionLabel actions={appActions} />
      {buttons?.length > 0 && <MessageButtonBar align="right" buttons={buttons} />}
      <QBox
        data-testid="agent-message"
        sx={{
          textAlign: 'left',
          // Fades the trailing edge while revealing, so newly-appearing text softens in rather
          // than snapping to full opacity — removed once the reveal finishes.
          ...(isRevealing && {
            maskImage: 'linear-gradient(to bottom, black calc(100% - 20px), transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 20px), transparent 100%)',
          }),
          // Matches the Eclipse "smallRegular" typography token (12px / 20px) — this box can't use
          // the variant prop directly since it wraps arbitrary markdown-rendered HTML, not QText.
          fontSize: '12px',
          lineHeight: '20px',
          overflowWrap: 'break-word',
          color: 'text.onSurface.background.mediumContrast',
          ...expandedTextColumnSx(isExpanded),
          '& a': {
            color: 'text.onSurface.background.link',
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
            '&:hover': { color: 'brand.primary.800' },
          },
          '& p': {
            margin: '0 0 0.75rem 0',
            '&:last-child': {
              marginBottom: 0,
            },
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            margin: '1rem 0 0.5rem 0',
            color: 'text.onSurface.background.mediumContrast',
            '&:first-of-type': {
              marginTop: 0,
            },
          },
          '& h1': { fontSize: '1.5rem', fontWeight: 600 },
          '& h2': { fontSize: '1.25rem', fontWeight: 600 },
          '& h3': { fontSize: '1.125rem', fontWeight: 600 },
          '& h4': { fontSize: '1rem', fontWeight: 600 },
          '& h5': { fontSize: '0.875rem', fontWeight: 600 },
          '& h6': { fontSize: '0.75rem', fontWeight: 600 },
          '& ul, & ol': {
            margin: '0 0 0.75rem 0',
            paddingLeft: '1.5rem',
          },
          '& li': {
            marginBottom: '0.25rem',
          },
          '& hr': {
            border: 'none',
            borderTop: '1px solid',
            borderColor: 'border.neutralPrimary.mediumContrast',
            margin: '0.75rem 0',
          },
          '& table': {
            borderCollapse: 'collapse',
            marginBottom: '0.75rem',
            width: '100%',
            fontSize: '12px',
          },
          '& td, & th': {
            padding: '0.375rem 0.5rem',
          },
          '& th': {
            fontWeight: 600,
            color: 'text.onSurface.background.lowContrast',
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.025em',
          },
          '& thead': {
            borderBottom: '1px solid',
            borderColor: 'border.neutralPrimary.mediumContrast',
          },
          '& tbody tr': {
            borderBottom: '1px solid',
            borderColor: 'border.neutralPrimary.mediumContrast',
            '&:last-child': {
              borderBottom: 'none',
            },
          },
          '& blockquote': {
            borderLeft: '3px solid',
            borderLeftColor: 'border.neutralPrimary.mediumContrast',
            margin: '0.75rem 0',
            paddingLeft: '0.75rem',
            paddingTop: '0.25rem',
            paddingBottom: '0.25rem',
            color: 'text.onSurface.background.lowContrast',
            fontStyle: 'italic',
            fontSize: '12px',
          },
          '& code': {
            color: 'brand.primary.700',
            backgroundColor: 'neutral.primary.200',
            borderRadius: '3px',
            padding: '0.1em 0.3em',
          },
          '& strong': {
            fontWeight: 600,
            color: 'text.onSurface.background.mediumContrast',
          },
          '& span.positive': {
            color: 'denotive.positive.700',
          },
          '& span.negative': {
            color: 'denotive.danger.700',
          },
        }}
      >
        <SourcesContext.Provider value={sources}>
          <Markdown
            components={markdownComponents}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizationSchema]]}
            remarkPlugins={[remarkGfm]}
          >
            {displayedContents}
          </Markdown>
        </SourcesContext.Provider>
      </QBox>
      {!isRevealing &&
        (isFeatureEnabled(FEATURE_FLAG_AI_COMP_ADMIN_AGENT) || isFeatureEnabled(FEATURE_FLAG_AGENT_CHARTING)) &&
        message.content &&
        message.content.length > 0 && <ContentBlockRenderer blocks={message.content} />}
      {!isRevealing && sources.length > 0 && <SourcesFooter sources={sources} />}
      {!isRevealing && onFeedback && (
        <FeedbackButtons
          feedback={feedback ?? null}
          feedbackResponse={feedbackResponse}
          onFeedback={(f) => onFeedback(message.id, f)}
          onFeedbackResponse={onFeedbackResponse ? async (text) => onFeedbackResponse(message.id, text) : undefined}
        />
      )}
    </QStack>
  );
};
